/**
 * Driver profile returned on login.
 */
export interface DriverProfile {
  driverNumber: string;
  driverName?: string | null;
  status?: string | null;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string; // ISO timestamp
}

export interface LoginResponse extends AuthTokens {
  driver: DriverProfile;
}

/**
 * Two-switch architecture.
 *  - server `rem_f31_switch`: '0' new, '1' active, '99' complete, '999' error/dead-call
 *  - local `dev_f31_switch_UPD`: 1 new, 2 pickup, 5 delivery, 99 complete (purge)
 */
export const RemSwitch = {
  New: '0',
  Active: '1',
  Complete: '99',
  Error: '999',
} as const;

export const DevSwitch = {
  New: 1,
  Pickup: 2,
  Delivery: 5,
  Complete: 99,
} as const;

/**
 * Dispatch routing (`DEV_f200_disp_switch`), confirmed by Martin (June 2026):
 *   1 = Shipper -> Consignee
 *   2 = Shipper -> Transfer (deliver to DOCK)
 *   4 = Transfer -> Consignee (pick up FROM DOCK)
 *   3 = unused
 */
export const DispatchSwitch = {
  ShipperToConsignee: '1',
  ShipperToDock: '2',
  DockToConsignee: '4',
} as const;

/**
 * Local Assignment record. Keyed by `remF04Id` (= API `recordId` = rem_f04_ID).
 * Field names mirror the FileMaker `rem_*` columns; the API DTO (camelCase) is
 * translated in `mapAssignmentDto`.
 */
export interface Assignment {
  remF04Id: string; // rem_f04_ID (primary key, also pod-status key)
  fmRecordId?: string | null; // FM internal record id from the DTO
  remF01User?: string | null; // driver number
  remF02Tag?: string | null; // PRO / bill tag
  remF31Switch: string; // server switch
  remDispatchType?: string | null; // DEV_f200_disp_switch (1/2/4)

  // Timestamps
  remModifiedTimestamp?: string | null;
  remInputDate?: string | null;
  remInputTime?: string | null;
  remShipDate?: string | null;
  remShipTime?: string | null;

  // Shipper
  remShipperName?: string | null; // rem_f19_shc_name
  remShipperAddress?: string | null; // rem_f20_shc_add
  remShipperCity?: string | null; // rem_f21_shc_city
  remShipperDetails?: string | null; // rem_f22_shc_details
  remShipperNote?: string | null; // rem_f23_tpe_shc_note
  remPickupFrom?: string | null; // rem_f29_pup_from
  remShipperPhone?: string | null;
  remShipperSuite?: string | null; // rem_f72_add_ship_suite

  // Consignee
  remConsigneeName?: string | null; // rem_f24_con_name
  remConsigneeAddress?: string | null; // rem_f25_con_add
  remConsigneeCity?: string | null; // rem_f26_con_city
  remConsigneeDetails?: string | null; // rem_f27_con_details
  remConsigneeNote?: string | null; // rem_f28_tpe_con_note
  remDeliveryTo?: string | null; // rem_f30_delivery_to
  remConsigneePhone?: string | null;
  remConsigneeSuite?: string | null; // rem_f73_add_con_suite

  // Shipment details
  remDispatchStatus?: string | null;
  remCallerName?: string | null;
  remCallerPhone?: string | null;
  remPiecesSummary?: string | null; // rem_f35_pcs_summary
  remWeightSummary?: string | null; // rem_f36_wgt_summary
  remCubicSummary?: string | null; // rem_f37_pcf_summary
  remPackageType?: string | null;
  remCommodity?: string | null; // rem_f39_commodity
  remService?: string | null;
  remRef1?: string | null; // rem_f41_ref1
  remRef2?: string | null; // rem_f42_ref2
  remRef3?: string | null; // rem_f43_ref3
  remRef4?: string | null; // rem_f44_ref4
  remUom?: string | null;

  // Scheduling
  remReadyAt?: string | null;
  remCloseAt?: string | null;

  // PRO / division
  remProNumber?: string | null; // DEV_f180_pro_number
  remSortValue?: string | null;
  remDivision?: string | null;

  // Flags
  remProbRequirement?: string | null; // rem_prob_requirement (temperature probe)
  remCancelDelivery?: string | null;

  // Local-only
  devF31SwitchUpd: number;
  receivedAt?: string | null;
  syncedAt?: string | null;
}

/** Shape of the assignment as serialized by the middleware (camelCase DTO). */
export interface AssignmentDtoApi {
  fmRecordId?: string;
  driverNumber?: string;
  tag?: string;
  recordId?: string;
  switch?: string;
  dispatchType?: string;
  modifiedTimestamp?: string | null;
  inputDate?: string | null;
  inputTime?: string | null;
  shipDate?: string | null;
  shipTime?: string | null;
  shipperName?: string | null;
  shipperAddress?: string | null;
  shipperCity?: string | null;
  shipperDetails?: string | null;
  shipperNote?: string | null;
  pickupFrom?: string | null;
  shipperPhone?: string | null;
  consigneeName?: string | null;
  consigneeAddress?: string | null;
  consigneeCity?: string | null;
  consigneeDetails?: string | null;
  consigneeNote?: string | null;
  deliveryTo?: string | null;
  consigneePhone?: string | null;
  dispatchStatus?: string | null;
  callerName?: string | null;
  callerPhone?: string | null;
  piecesSummary?: string | null;
  weightSummary?: string | null;
  cubicSummary?: string | null;
  packageType?: string | null;
  commodity?: string | null;
  service?: string | null;
  ref1?: string | null;
  ref2?: string | null;
  ref3?: string | null;
  ref4?: string | null;
  uom?: string | null;
  shipperSuite?: string | null;
  consigneeSuite?: string | null;
  readyAt?: string | null;
  closeAt?: string | null;
  proNumber?: string | null;
  sortValue?: string | null;
  division?: string | null;
  podRequirement?: string | null;
  cancelDelivery?: string | null;
}

/** Translate the middleware DTO into the local Assignment shape. */
export function mapAssignmentDto(d: AssignmentDtoApi): Assignment {
  return {
    remF04Id: d.recordId ?? d.fmRecordId ?? '',
    fmRecordId: d.fmRecordId ?? null,
    remF01User: d.driverNumber ?? null,
    remF02Tag: d.tag ?? null,
    remF31Switch: d.switch ?? RemSwitch.New,
    remDispatchType: d.dispatchType ?? null,
    remModifiedTimestamp: d.modifiedTimestamp ?? null,
    remInputDate: d.inputDate ?? null,
    remInputTime: d.inputTime ?? null,
    remShipDate: d.shipDate ?? null,
    remShipTime: d.shipTime ?? null,
    remShipperName: d.shipperName ?? null,
    remShipperAddress: d.shipperAddress ?? null,
    remShipperCity: d.shipperCity ?? null,
    remShipperDetails: d.shipperDetails ?? null,
    remShipperNote: d.shipperNote ?? null,
    remPickupFrom: d.pickupFrom ?? null,
    remShipperPhone: d.shipperPhone ?? null,
    remShipperSuite: d.shipperSuite ?? null,
    remConsigneeName: d.consigneeName ?? null,
    remConsigneeAddress: d.consigneeAddress ?? null,
    remConsigneeCity: d.consigneeCity ?? null,
    remConsigneeDetails: d.consigneeDetails ?? null,
    remConsigneeNote: d.consigneeNote ?? null,
    remDeliveryTo: d.deliveryTo ?? null,
    remConsigneePhone: d.consigneePhone ?? null,
    remConsigneeSuite: d.consigneeSuite ?? null,
    remDispatchStatus: d.dispatchStatus ?? null,
    remCallerName: d.callerName ?? null,
    remCallerPhone: d.callerPhone ?? null,
    remPiecesSummary: d.piecesSummary ?? null,
    remWeightSummary: d.weightSummary ?? null,
    remCubicSummary: d.cubicSummary ?? null,
    remPackageType: d.packageType ?? null,
    remCommodity: d.commodity ?? null,
    remService: d.service ?? null,
    remRef1: d.ref1 ?? null,
    remRef2: d.ref2 ?? null,
    remRef3: d.ref3 ?? null,
    remRef4: d.ref4 ?? null,
    remUom: d.uom ?? null,
    remReadyAt: d.readyAt ?? null,
    remCloseAt: d.closeAt ?? null,
    remProNumber: d.proNumber ?? null,
    remSortValue: d.sortValue ?? null,
    remDivision: d.division ?? null,
    remProbRequirement: d.podRequirement ?? null,
    remCancelDelivery: d.cancelDelivery ?? null,
    devF31SwitchUpd: 1,
  };
}

/**
 * Validated FileMaker container fields on API MOBILE_LOG_MEDIA.
 * Signatures use the *_sig_capture_UPD fields (defect #15); BOL/POD masters
 * hold the optional document photos.
 */
export const MediaFields = {
  pickupSignature: 'dev_f03_pup_sig_capture_UPD',
  deliverySignature: 'dev_f05_del_sig_capture_UPD',
  // The FileMaker field index is 2-digit zero-padded (f07..f11 / f12..f16), so
  // pad the number rather than hard-coding an `f0` prefix -- otherwise slots >=
  // 10 render as `dev_f010`/`dev_f011` and the middleware rejects them as an
  // invalid container field (400). Must match FmLogMediaFields on the server.
  pickupPhoto: (slot: number) => `dev_f${fmIndex(slot + 6)}_pup_pic${slot}_UPD`, // 1->dev_f07.., 5->dev_f11
  deliveryPhoto: (slot: number) => `dev_f${fmIndex(slot + 11)}_del_pic${slot}_UPD`, // 1->dev_f12.., 5->dev_f16
  bolMaster: 'dev_BOL_MASTER',
  podMaster: 'dev_POD_MASTER',
} as const;

/** 2-digit zero-padded FileMaker field index (7 -> "07", 10 -> "10"). */
function fmIndex(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Correct container-field names that an earlier build mis-generated (the
 * `dev_f0${n}` bug produced `dev_f010`/`dev_f011` for pickup pics 4-5). Applied
 * to already-queued media on upload so items captured before the fix still send
 * without a re-capture. Returns the input unchanged when nothing needs fixing.
 */
const MEDIA_FIELD_REPAIRS: Record<string, string> = {
  dev_f010_pup_pic4_UPD: 'dev_f10_pup_pic4_UPD',
  dev_f011_pup_pic5_UPD: 'dev_f11_pup_pic5_UPD',
};

export function repairMediaFieldName(fieldName: string): string {
  return MEDIA_FIELD_REPAIRS[fieldName] ?? fieldName;
}

/**
 * Container fields verified end-to-end via the Assignment `_PRESENT` flags
 * (the four legal artifacts). Maps container field -> _PRESENT flag.
 */
export const PresentFlagByField: Record<string, string> = {
  'dev_f03_pup_sig_capture_UPD': 'dev_f03_pup_sig_capture_UPD_PRESENT',
  'dev_f05_del_sig_capture_UPD': 'dev_f05_del_sig_capture_UPD_PRESENT',
  'dev_BOL_MASTER': 'dev_BOL_MASTER_PRESENT',
  'dev_POD_MASTER': 'dev_POD_MASTER_PRESENT',
};

export function isVerifiableField(fieldName: string): boolean {
  return fieldName in PresentFlagByField;
}

/**
 * True when a queued media item is a captured signature (legal POD/BOL sign).
 * Used by the POD recovery "PODs only" filter to mirror native.
 */
export function isSignatureMedia(m: { kind: MediaKind; fieldName: string }): boolean {
  return (
    m.kind === 'pickup-signature' ||
    m.kind === 'delivery-signature' ||
    m.fieldName === MediaFields.pickupSignature ||
    m.fieldName === MediaFields.deliverySignature
  );
}

/**
 * AVS (accessorial) field maps. Inside/Tailgate/Residential are Yes/No on both
 * sides (default No); DG is pickup-only.
 */
export const AvsPickupFields = {
  inside: 'DEV_F300_inside_pup',
  tailgate: 'dev_f58_pup_tg_UPD',
  residential: 'dev_f60_pup_res_UPD',
  dangerousGoods: 'dev_f62_pup_dg_UPD',
} as const;

export const AvsDeliveryFields = {
  inside: 'DEV_F301_inside_del',
  tailgate: 'dev_f59_del_tg_UPD',
  residential: 'dev_f61_del_res_UPD',
} as const;

/** Per-bill capture FM fields. */
export const CaptureFields = {
  pickupNotes: 'dev_f68_notes_PUP',
  deliveryNotes: 'dev_f69_notes_DEL',
  pickupName: 'dev_f17_pup_name_capture_UPD',
  deliveryName: 'dev_f18_del_name_capture_UPD',
  deliveryOsd: 'dev_C02_DEL_OSD_Status',
  /** log_L08_f31_switch — complete / removed from device (99). */
  switch99: 'log_L08_f31_switch',
  /** log_L08_f31_switch — dead call / reset (999). */
  switch999: 'log_L08_f31_switch',
} as const;

/** OSD options for delivery (defect #19); default Clear. */
export const OsdOptions = ['Clear', 'Over', 'Short', 'Damage', 'Temperature', 'Other'] as const;
export type OsdValue = (typeof OsdOptions)[number];

/**
 * Fixed log-type strings + builders for address-interpolated ones. Mirrors the
 * exact strings Martin parses on the FileMaker side.
 */
export const LogTypes = {
  // Session
  login: 'LOG IN',
  logout: 'LOG OUT',
  // Status / terminal
  mealStart: 'Start Break MEAL',
  mealEnd: 'End Break MEAL',
  coffeeStart: 'Start Break COFFEE',
  coffeeEnd: 'End Break COFFEE',
  backToBase: 'RETURNING TO TERMINAL',
  arrivedAtTerminal: 'ARRIVED AT TERMINAL',
  unloadedAtTerminalDock: 'UNLOADED AT TERMINAL DOCK',
  loadedAtTerminalDock: 'Loaded at Terminal dock',
  // GPS -- native logs every periodic ping as 'gps break' (exact FileMaker parity).
  gpsTick: 'gps break',
  trackingGap: 'TRACKING GAP',
} as const;

export type LogType = string;

/** Builders for address-interpolated log types (exact Flutter parity). */
export const buildLog = {
  // Pickup
  // PUP/DEL prefixes on the en-route process events (Martin, July 24 2026):
  // he parses the first token to manage the log as pickup vs delivery.
  enRouteToPickup: (addr: string) => `PUP EN ROUTE TO ${addr}`,
  enRoutePickupCanceled: (addr: string) => `EN ROUTE ***CANCELED TO ${addr}`,
  arrivedAtPickup: (addr: string) => `PUP ARRIVED AT ${addr}`,
  loadingAt: (addr: string) => `LOADING AT ${addr}`,
  temperatureProbe: (addr: string) => `TEMPERATURE PROBE ${addr}`,
  deadCallTemperature: () => 'DEAD CALL Temperature',
  finishLoadingAt: (addr: string) => `FINISH LOADING AT ${addr}`,
  roomLeft: (room: string) => `ROOM LEFT ${room}`,
  deadCallNoFreightPickup: () => 'DEAD CALL No Freight',
  pupDepartedFrom: (addr: string) => `PUP DEPARTED FROM ${addr}`,
  // Delivery
  enRouteToDelivery: (addr: string) => `DEL EN ROUTE TO ${addr}`,
  enRouteDeliveryCancelled: (addr: string) => `EN ROUTE ***CANCELLED TO ${addr}`,
  arrivedAtDelivery: (addr: string) => `DEL ARRIVED AT ${addr}`,
  unloadingAt: (addr: string) => `UNLOADING AT ${addr}`,
  noFreightDelivery: (addr: string) => `*** No Freight ${addr}`,
  finishUnloadingAt: (addr: string) => `FINISH UNLOADING AT ${addr}`,
  // DEL prefix mirrors the pickup side's PUP DEPARTED FROM; Martin parses the
  // first token to close the record as pickup vs delivery.
  departedFrom: (addr: string) => `DEL DEPARTED FROM ${addr}`,
  // Dead call / undeliverable (Martin #45/#46): first token + location + reason.
  deadCallPickup: (loc: string, reason: string) => `DEAD CALL & ${loc} & ${reason}`,
  deadCallDelivery: (loc: string, reason: string) => `UNDELIVERABLE & ${loc} & ${reason}`,
  removedFromDeliveryList: (driver: string) =>
    `LOGGED REMOVED BY '${driver}' FROM DELIVERY LIST`,
};

/**
 * A queued log entry awaiting (or having attempted) push to /api/log.
 */
export interface LogEntry {
  id: string;
  logType: LogType;
  display?: string;
  assignmentId?: string;
  tag?: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  capturedAt: string; // ISO
  additionalFields?: Record<string, string | number | null>;
  attempts: number;
  status: 'pending' | 'inflight' | 'sent' | 'quarantined';
  lastError?: string;
  serverRecordId?: string;
}

export type MediaKind =
  | 'pickup-photo'
  | 'delivery-photo'
  | 'pickup-signature'
  | 'delivery-signature'
  | 'bol-doc'
  | 'pod-doc';

/**
 * A queued media upload (signature / photo / document image).
 *
 * Two-step container flow: media is linked to its log entry by `logClientId`.
 * The FileMaker record id (`fmRecordId`) is resolved AFTER that log is pushed,
 * then the container is uploaded to the matching API MOBILE_LOG_MEDIA record.
 */
export interface MediaQueueItem {
  id: string;
  kind: MediaKind;
  assignmentId: string; // remF04Id (also the pod-status key)
  logClientId: string; // the log entry that carries this media
  fmRecordId?: string | null; // resolved from the pushed log
  fieldName: string;
  /**
   * Image/signature payload, stored as an ArrayBuffer rather than a Blob.
   * iOS/WebKit does not reliably retain a Blob's backing bytes in IndexedDB
   * across an app relaunch, so a Blob read back in a later session (e.g. a POD
   * Recovery re-send) can be zero-length and the upload posts an empty file
   * (middleware then rejects it with 400). ArrayBuffers are fully serialised by
   * structured clone and survive the round-trip. Reconstruct a Blob at upload.
   */
  bytes: ArrayBuffer;
  /**
   * @deprecated Legacy rows persisted the payload as a Blob. Read as a fallback
   * and migrated to `bytes` on upload; on iOS the bytes may already be gone.
   */
  blob?: Blob;
  filename: string;
  mimeType: string;
  tag?: string | null; // PRO
  driverNumber?: string | null;
  /** Device GPS at media capture time (Martin #47). */
  latitude?: number | null;
  longitude?: number | null;
  capturedAt: string;
  uploadedAt?: string | null;
  // End-to-end POD acknowledgment
  verified?: boolean;
  verifiedAt?: string | null;
  attempts: number;
  status: 'pending' | 'inflight' | 'uploaded' | 'quarantined';
  lastError?: string;
}

export interface CityRecord {
  id: string;
  name: string;
  province?: string | null;
}

export interface EquipmentRecord {
  id: string;
  description: string;
  status?: string | null;
}
